var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { createPublicClient, http } from 'viem';
import invariant from 'tiny-invariant';
import { PUBLIC_RPCS } from '../../config/networks';
import { MaxSigDeadline } from '../../../types/config/constants';
import { Permit2Abi } from '../../../types/abis/Permit2Abi';
import { SupportedNetworks } from '../../config/networkAddresses';
import { isNativeToken } from '../../quoting-engine/network/utils/isNativeToken';
import { PERMIT_BATCH_TYPES } from '../../../types/evm';
import { validatePermitDetails } from '../../../types/evm';
import { CURRENT_VERSION } from '../../config/environment';
function convertBigIntToString(obj) {
    for (var key in obj) {
        if (typeof obj[key] === 'bigint') {
            obj[key] = obj[key].toString();
        }
        else if (typeof obj[key] === 'object') {
            convertBigIntToString(obj[key]);
        }
    }
}
/**
 * Builds the transfer details for batch transfer with permit2.
 * @param {IBuildBatchTransferDetails} options - The options for building the transfer details.
 * @returns {Promise<{ permitBatchDetails: PermitBatch, permitSignature: Hex }>} The permit batch details and permit signature.
 */
//TODO add option to only return permitBatchDetails if wallet is not provided so we can expose as api
export function buildPermit2Details(_a) {
    return __awaiter(this, arguments, Promise, function (_b) {
        var expirationSeconds, clientConfig, client, _c, executor, permit2, _d, nonNativeTokens, nonNativeAmounts, permit2Infos, nonces, permit2Details, permitBatchDetails, domain, permitSignature;
        var tokensIn = _b.tokensIn, amounts = _b.amounts, spender = _b.spender, network = _b.network, ownerAccount = _b.ownerAccount;
        return __generator(this, function (_e) {
            switch (_e.label) {
                case 0:
                    expirationSeconds = BigInt(60 * 60);
                    clientConfig = SupportedNetworks(CURRENT_VERSION)[network];
                    client = createPublicClient({
                        chain: clientConfig.chain,
                        transport: http(PUBLIC_RPCS[network]),
                    });
                    _c = SupportedNetworks(CURRENT_VERSION)[network], executor = _c.executor, permit2 = _c.permit2;
                    _d = tokensIn.reduce(function (acc, token, index) {
                        if (isNativeToken(token)) {
                            return acc;
                        }
                        acc.nonNativeTokens.push(token);
                        acc.nonNativeAmounts.push(amounts[index]);
                        return acc;
                    }, { nonNativeTokens: [], nonNativeAmounts: [] }), nonNativeTokens = _d.nonNativeTokens, nonNativeAmounts = _d.nonNativeAmounts;
                    return [4 /*yield*/, client.multicall({
                            // @ts-ignore
                            contracts: nonNativeTokens.map(function (token) {
                                return {
                                    address: permit2,
                                    abi: Permit2Abi,
                                    functionName: 'allowance',
                                    args: [ownerAccount.address, token, executor],
                                };
                            }),
                            allowFailure: false,
                        })];
                case 1:
                    permit2Infos = _e.sent();
                    nonces = permit2Infos.map(function (info) { return info[2]; });
                    permit2Details = nonNativeTokens.map(function (token, index) { return ({
                        token: token,
                        amount: BigInt(nonNativeAmounts[index]),
                        expiration: BigInt(Math.floor(Date.now() / 1000)) + expirationSeconds,
                        nonce: BigInt(nonces[index]),
                    }); });
                    permitBatchDetails = {
                        details: permit2Details,
                        spender: spender,
                        sigDeadline: BigInt(Math.floor(Date.now() / 1000)) + expirationSeconds,
                    };
                    invariant(MaxSigDeadline.gte(permitBatchDetails.sigDeadline), 'SIG_DEADLINE_OUT_OF_RANGE');
                    //@ts-ignore
                    permitBatchDetails.details.forEach(validatePermitDetails);
                    domain = {
                        name: 'Permit2',
                        chainId: network,
                        verifyingContract: permit2,
                    };
                    return [4 /*yield*/, ownerAccount._signTypedData(domain, PERMIT_BATCH_TYPES, permitBatchDetails)];
                case 2:
                    permitSignature = _e.sent();
                    convertBigIntToString(permitBatchDetails);
                    return [2 /*return*/, {
                            permitBatchDetails: permitBatchDetails,
                            permitSignature: permitSignature,
                        }];
            }
        });
    });
}
